import { Fragment, ReactNode, useMemo } from "react";
import { UseQueryResult } from "react-query";
import { BackendErrorMessage } from "./ErrorMessage";
import { FullPageLoadingSpinner, LoadingSpinner } from "./LoadingSpinner";

type PartialUseQueryResult = Pick<
  UseQueryResult,
  "status" | "refetch" | "error"
>;
interface QueryWrapperProps {
  children: ReactNode | ReactNode[];
  useQueryResult: PartialUseQueryResult | PartialUseQueryResult[];
  loaderType?: "big" | "small";
}

type QueryWrapperStatus = "idle" | "loading" | "error" | "success";

const queryWrapperStatusPriorityMap: Record<QueryWrapperStatus, number> = {
  error: 1,
  loading: 2,
  success: 3,

  idle: 100,
};

export function QueryWrapper({
  children,
  useQueryResult,
  loaderType = "small",
}: QueryWrapperProps) {
  const useQueryResultArray = useMemo(() => {
    return Array.isArray(useQueryResult) ? useQueryResult : [useQueryResult];
  }, [useQueryResult]);

  const [status, error]: [QueryWrapperStatus, Error?] = useMemo(() => {
    let resultStatus: QueryWrapperStatus = "idle";
    let error: Error | undefined = undefined;
    for (let i = 0; i < useQueryResultArray.length; i++) {
      const value = useQueryResultArray[i];
      if (
        !resultStatus ||
        queryWrapperStatusPriorityMap[value.status] <
          queryWrapperStatusPriorityMap[resultStatus]
      ) {
        resultStatus = value.status;
      }
      if (!error && resultStatus === "error" && value.error instanceof Error) {
        // only set the first error of the array
        error = value.error;
      }
    }

    return [resultStatus, error];
  }, [useQueryResultArray]);

  if (status === "loading" || status === "idle") {
    return loaderType === "big" ? (
      <FullPageLoadingSpinner />
    ) : (
      <LoadingSpinner />
    );
  } else if (status === "error") {
    return <BackendErrorMessage error={error} />;
  } else {
    return <Fragment>{children}</Fragment>;
  }
}
